import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  BulbOutlined,
  CheckOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { Chapter } from '../types';
import type { MarketingActionSignal, MarketingInspirationItem, MarketingInspirationRun } from './types';
import { sortMarketingInspirationItems } from './inspiration';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

interface MarketingInspirationModuleProps {
  apiBase: string;
  bookId: string;
  userId: string;
  sourceLanguage: string;
  editionLanguage: string;
  actionSignal?: MarketingActionSignal | null;
  onSourcesChanged?: () => void;
}

interface InspirationBookResponse {
  chapters?: Chapter[];
}

type EditorState = Partial<MarketingInspirationItem> & { id?: string };

const TYPE_COLORS: Record<string, string> = {
  scene: 'blue',
  quote: 'gold',
  vibe: 'purple',
};

const createBlankItem = (): EditorState => ({
  type: 'scene',
  title: '',
  text: '',
  status: 'draft',
  spoilerLevel: 'medium',
  characterNames: [],
  timePeriods: [],
  locationNames: [],
  tags: [],
  vibeTags: [],
  contentFlags: [],
  chapterGuids: [],
  chapterTitles: [],
  sourceExcerpt: '',
  quoteText: '',
  sceneVideo: {
    sceneMode: '',
    prompt: '',
    beats: [],
    dialogueOverlayLines: [],
  },
  usageHints: {
    characterStudio: '',
    videoStudio: '',
    socialSlides: '',
  },
});

const csvToArray = (values?: string[]): string[] => (Array.isArray(values) ? values.filter(Boolean) : []);

const formatTimestamp = (value?: string | null): string => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const getErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const data = await response.json();
    return String(data?.detail || data?.message || fallback);
  } catch {
    return fallback;
  }
};

const MarketingInspirationModule: React.FC<MarketingInspirationModuleProps> = ({
  apiBase,
  bookId,
  userId,
  sourceLanguage,
  editionLanguage,
  actionSignal,
  onSourcesChanged,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<MarketingInspirationItem[]>([]);
  const [runs, setRuns] = useState<MarketingInspirationRun[]>([]);
  const [chapters, setChapters] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [rerunModalOpen, setRerunModalOpen] = useState(false);
  const [selectedRerunChapterGuids, setSelectedRerunChapterGuids] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [spoilerFilter, setSpoilerFilter] = useState<string>('all');
  const [chapterFilter, setChapterFilter] = useState<string>('all');
  const [characterFilter, setCharacterFilter] = useState<string>('all');
  const [contentFlagFilter, setContentFlagFilter] = useState<string>('all');

  const loadData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const editionParams = new URLSearchParams({ userId, sourceLanguage, editionLanguage });
      const [itemsResponse, runsResponse, bookResponse] = await Promise.all([
        fetch(`${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration?${editionParams.toString()}`),
        fetch(`${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration/runs?${editionParams.toString()}`),
        fetch(`${apiBase}/uploadedbooks/book/${encodeURIComponent(bookId)}?slim=true`),
      ]);
      if (!itemsResponse.ok) {
        throw new Error(await getErrorMessage(itemsResponse, 'Failed to load inspiration items.'));
      }
      if (!runsResponse.ok) {
        throw new Error(await getErrorMessage(runsResponse, 'Failed to load inspiration runs.'));
      }
      const itemsPayload = await itemsResponse.json();
      const runsPayload = await runsResponse.json();
      const bookPayload = bookResponse.ok ? ((await bookResponse.json()) as InspirationBookResponse) : {};
      const nextItems = sortMarketingInspirationItems(Array.isArray(itemsPayload?.items) ? itemsPayload.items : []);
      const nextRuns = Array.isArray(runsPayload?.items) ? runsPayload.items : [];
      const nextChapters = (bookPayload?.chapters || [])
        .map(chapter => ({
          value: String(chapter.chapter_guid || '').trim(),
          label: String(chapter.chapter_name || chapter.chapter_guid || 'Untitled chapter').trim(),
        }))
        .filter(option => option.value);
      setItems(nextItems);
      setRuns(nextRuns);
      setChapters(nextChapters);
      setSelectedItemId(prev => {
        if (prev && nextItems.some(item => item.id === prev)) return prev;
        return nextItems[0]?.id || '';
      });
    } catch (error: any) {
      message.error(error?.message || 'Failed to load inspiration library.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, bookId, editionLanguage, sourceLanguage, userId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!actionSignal?.ts || actionSignal.action !== 'refresh') return;
    void loadData();
  }, [actionSignal, loadData]);

  const selectedItem = useMemo(
    () => items.find(item => item.id === selectedItemId) || null,
    [items, selectedItemId]
  );

  useEffect(() => {
    if (!selectedItem) {
      if (!editor?.id) {
        setEditor(createBlankItem());
      }
      return;
    }
    setEditor({
      ...selectedItem,
      characterNames: csvToArray(selectedItem.characterNames),
      timePeriods: csvToArray(selectedItem.timePeriods),
      locationNames: csvToArray(selectedItem.locationNames),
      tags: csvToArray(selectedItem.tags),
      vibeTags: csvToArray(selectedItem.vibeTags),
      contentFlags: csvToArray(selectedItem.contentFlags),
      chapterGuids: csvToArray(selectedItem.chapterGuids),
      chapterTitles: csvToArray(selectedItem.chapterTitles),
      sceneVideo: {
        sceneMode: selectedItem.sceneVideo?.sceneMode || '',
        prompt: selectedItem.sceneVideo?.prompt || '',
        beats: csvToArray(selectedItem.sceneVideo?.beats),
        dialogueOverlayLines: csvToArray(selectedItem.sceneVideo?.dialogueOverlayLines),
      },
      usageHints: {
        characterStudio: selectedItem.usageHints?.characterStudio || '',
        videoStudio: selectedItem.usageHints?.videoStudio || '',
        socialSlides: selectedItem.usageHints?.socialSlides || '',
      },
    });
  }, [selectedItem]);

  const chapterOptionMap = useMemo(() => new Map(chapters.map(option => [option.value, option.label])), [chapters]);
  const characterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          items.flatMap(item => item.characterNames || []).map(value => String(value || '').trim()).filter(Boolean)
        )
      ).map(value => ({ value, label: value })),
    [items]
  );
  const contentFlagOptions = useMemo(
    () =>
      Array.from(
        new Set(
          items.flatMap(item => item.contentFlags || []).map(value => String(value || '').trim()).filter(Boolean)
        )
      ).map(value => ({ value, label: value })),
    [items]
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return sortMarketingInspirationItems(items).filter(item => {
      if (typeFilter !== 'all' && item.type !== typeFilter) return false;
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (spoilerFilter !== 'all' && item.spoilerLevel !== spoilerFilter) return false;
      if (chapterFilter !== 'all' && !(item.chapterGuids || []).includes(chapterFilter)) return false;
      if (characterFilter !== 'all' && !(item.characterNames || []).includes(characterFilter)) return false;
      if (contentFlagFilter !== 'all' && !(item.contentFlags || []).includes(contentFlagFilter)) return false;
      if (!query) return true;
      const haystack = [
        item.title,
        item.text,
        item.quoteText,
        ...(item.timePeriods || []),
        item.sceneVideo?.sceneMode,
        item.sceneVideo?.prompt,
        ...(item.sceneVideo?.beats || []),
        ...(item.sceneVideo?.dialogueOverlayLines || []),
        ...(item.characterNames || []),
        ...(item.tags || []),
        ...(item.vibeTags || []),
        ...(item.contentFlags || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [chapterFilter, characterFilter, contentFlagFilter, items, search, spoilerFilter, statusFilter, typeFilter]);

  const updateEditor = <K extends keyof EditorState>(key: K, value: EditorState[K]) => {
    setEditor(prev => ({ ...(prev || createBlankItem()), [key]: value }));
  };

  const saveItem = async () => {
    if (!editor) return;
    setSaving(true);
    try {
      const payload = {
        userId,
        sourceLanguage,
        editionLanguage,
        type: editor.type,
        title: editor.title,
        text: editor.text,
        status: editor.status,
        spoilerLevel: editor.spoilerLevel,
        characterNames: editor.characterNames || [],
        locationNames: editor.locationNames || [],
        tags: editor.tags || [],
        vibeTags: editor.vibeTags || [],
        contentFlags: editor.contentFlags || [],
        chapterGuids: editor.chapterGuids || [],
        chapterTitles: (editor.chapterGuids || [])
          .map(chapterGuid => chapterOptionMap.get(chapterGuid) || '')
          .filter(Boolean),
        sourceExcerpt: editor.sourceExcerpt,
        quoteText: editor.type === 'quote' ? editor.quoteText : '',
        timePeriods: editor.timePeriods || [],
        sceneVideo: editor.type === 'scene'
          ? {
              sceneMode: editor.sceneVideo?.sceneMode || '',
              prompt: editor.sceneVideo?.prompt || '',
              beats: editor.sceneVideo?.beats || [],
              dialogueOverlayLines: editor.sceneVideo?.dialogueOverlayLines || [],
            }
          : {},
        usageHints: editor.usageHints || {},
      };
      const isExisting = Boolean(editor.id);
      const response = await fetch(
        isExisting
          ? `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration/items/${encodeURIComponent(editor.id || '')}`
          : `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration/items`,
        {
          method: isExisting ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to save inspiration item.'));
      }
      const data = await response.json();
      const nextItem = (data?.item || null) as MarketingInspirationItem | null;
      message.success(isExisting ? 'Inspiration item updated.' : 'Inspiration item created.');
      await loadData();
      if (nextItem?.id) {
        setSelectedItemId(nextItem.id);
      }
      onSourcesChanged?.();
    } catch (error: any) {
      message.error(error?.message || 'Failed to save inspiration item.');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (itemId: string) => {
    setSaving(true);
    try {
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration/items/${encodeURIComponent(itemId)}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, sourceLanguage, editionLanguage }),
        }
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to delete inspiration item.'));
      }
      message.success('Inspiration item deleted.');
      setSelectedItemId('');
      setEditor(createBlankItem());
      await loadData();
      onSourcesChanged?.();
    } catch (error: any) {
      message.error(error?.message || 'Failed to delete inspiration item.');
    } finally {
      setSaving(false);
    }
  };

  const clearAllInspirationData = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, sourceLanguage, editionLanguage }),
        }
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to clear inspiration data.'));
      }
      const data = await response.json();
      const deletedItems = Number(data?.deleted?.items || 0);
      const deletedRuns = Number(data?.deleted?.runs || 0);
      const deletedLocks = Number(data?.deleted?.locks || 0);
      setSelectedItemId('');
      setEditor(createBlankItem());
      setSelectedRerunChapterGuids([]);
      message.success(`Cleared inspiration data: ${deletedItems} items, ${deletedRuns} runs, ${deletedLocks} locks.`);
      await loadData();
      onSourcesChanged?.();
    } catch (error: any) {
      message.error(error?.message || 'Failed to clear inspiration data.');
    } finally {
      setSaving(false);
    }
  };

  const queueExtraction = async (chapterGuids?: string[]) => {
    setSaving(true);
    try {
      const response = await fetch(
        chapterGuids?.length
          ? `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration/extract/chapters`
          : `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/inspiration/extract`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            sourceLanguage,
            editionLanguage,
            chapterGuids: chapterGuids?.length ? chapterGuids : undefined,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to queue inspiration extraction.'));
      }
      message.success(chapterGuids?.length ? 'Chapter rerun queued.' : 'Book extraction queued.');
      setRerunModalOpen(false);
      setSelectedRerunChapterGuids([]);
      await loadData();
    } catch (error: any) {
      message.error(error?.message || 'Failed to queue inspiration extraction.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Spin spinning={loading} style={{ width: '100%' }}>
      <Space direction="vertical" size={12} style={{ width: '100%', paddingRight: 4 }}>
        <Card
          size="small"
          title={
            <Space>
              <BulbOutlined />
              <span>Inspiration Library · {editionLanguage}</span>
            </Space>
          }
          extra={
            <Space wrap>
              <Button type="primary" icon={<SyncOutlined />} onClick={() => void queueExtraction()} loading={saving}>
                Extract From Book
              </Button>
              <Button onClick={() => setRerunModalOpen(true)} loading={saving}>
                Rerun Chapters
              </Button>
              <Popconfirm
                title="Clear all inspiration data?"
                description={`This deletes inspiration items, extraction runs, and lock history for the ${editionLanguage} edition.`}
                onConfirm={() => void clearAllInspirationData()}
                okText="Clear All"
                okButtonProps={{ danger: true, loading: saving }}
              >
                <Button danger loading={saving}>
                  Clear All
                </Button>
              </Popconfirm>
              <Button icon={<PlusOutlined />} onClick={() => {
                setSelectedItemId('');
                setEditor(createBlankItem());
              }}>
                New Item
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => void loadData()}>
                Refresh
              </Button>
            </Space>
          }
        >
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text type="secondary">
              Extract scenes, exact quotes, and vibes from the book, then approve or hand-edit them before using them in Character Studio or Video Studio.
            </Text>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <Input.Search
                  placeholder="Search inspiration..."
                  allowClear
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                />
              </Col>
              <Col xs={12} md={4}>
                <Select
                  value={typeFilter}
                  onChange={setTypeFilter}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'all', label: 'All Types' },
                    { value: 'scene', label: 'Scenes' },
                    { value: 'quote', label: 'Quotes' },
                    { value: 'vibe', label: 'Vibes' },
                  ]}
                />
              </Col>
              <Col xs={12} md={4}>
                <Select
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'all', label: 'All Statuses' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'draft', label: 'Draft' },
                    { value: 'skipped', label: 'Skipped' },
                  ]}
                />
              </Col>
              <Col xs={12} md={4}>
                <Select
                  value={spoilerFilter}
                  onChange={setSpoilerFilter}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'all', label: 'All Spoilers' },
                    { value: 'low', label: 'Low' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'high', label: 'High' },
                  ]}
                />
              </Col>
              <Col xs={12} md={4}>
                <Select
                  value={chapterFilter}
                  onChange={setChapterFilter}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'all', label: 'All Chapters' },
                    ...chapters,
                  ]}
                  showSearch
                  optionFilterProp="label"
                />
              </Col>
              <Col xs={12} md={4}>
                <Select
                  value={characterFilter}
                  onChange={setCharacterFilter}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'all', label: 'All Characters' },
                    ...characterOptions,
                  ]}
                  showSearch
                  optionFilterProp="label"
                />
              </Col>
              <Col xs={12} md={4}>
                <Select
                  value={contentFlagFilter}
                  onChange={setContentFlagFilter}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'all', label: 'All Content Flags' },
                    ...contentFlagOptions,
                  ]}
                  showSearch
                  optionFilterProp="label"
                />
              </Col>
            </Row>
          </Space>
        </Card>

        <Row gutter={[12, 12]} align="top">
          <Col xs={24} lg={10}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Card size="small" title={`Items (${filteredItems.length})`}>
                {filteredItems.length ? (
                  <List
                    dataSource={filteredItems}
                    renderItem={item => (
                      <List.Item
                        style={{
                          cursor: 'pointer',
                          borderRadius: 10,
                          paddingInline: 8,
                          background: item.id === selectedItemId ? 'rgba(22, 119, 255, 0.07)' : undefined,
                        }}
                        onClick={() => setSelectedItemId(item.id)}
                      >
                        <Space direction="vertical" size={6} style={{ width: '100%' }}>
                          <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Space wrap size={6}>
                              <Tag color={TYPE_COLORS[item.type] || 'default'}>{item.type}</Tag>
                              <Tag>{item.status || 'draft'}</Tag>
                              {item.origin ? <Tag>{item.origin}</Tag> : null}
                            </Space>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {formatTimestamp(item.updatedAt || item.createdAt)}
                            </Text>
                          </Space>
                          <Text strong>{item.title || 'Untitled inspiration'}</Text>
                          <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
                            {item.quoteText || item.text}
                          </Paragraph>
                          <Space wrap size={[6, 6]}>
                            {(item.characterNames || []).slice(0, 3).map(name => (
                              <Tag key={`${item.id}-character-${name}`}>{name}</Tag>
                            ))}
                            {(item.timePeriods || []).slice(0, 2).map(period => (
                              <Tag key={`${item.id}-period-${period}`} color="cyan">
                                {period}
                              </Tag>
                            ))}
                            {(item.vibeTags || []).slice(0, 3).map(tag => (
                              <Tag key={`${item.id}-vibe-${tag}`} color="purple">
                                {tag}
                              </Tag>
                            ))}
                          </Space>
                        </Space>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No inspiration items match the current filters." />
                )}
              </Card>

              <Card size="small" title="Run History">
                {runs.length ? (
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {runs.slice(0, 8).map(run => (
                      <div
                        key={run.id}
                        style={{
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 10,
                          padding: '8px 10px',
                          background: 'var(--surface-raised)',
                        }}
                      >
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Space wrap size={6}>
                              <Tag color={run.status === 'completed' ? 'green' : run.status === 'failed' ? 'red' : 'blue'}>
                                {run.status || 'queued'}
                              </Tag>
                              <Tag>{run.scope || 'book'}</Tag>
                              {run.modelId ? <Tag>{run.modelId}</Tag> : null}
                              {run.apiModel && run.apiModel !== run.modelId ? (
                                <Tag>
                                  {[run.modelProvider, run.apiModel].filter(Boolean).join(' · ')}
                                </Tag>
                              ) : null}
                            </Space>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {formatTimestamp(run.updatedAt || run.startedAt || run.createdAt)}
                            </Text>
                          </Space>
                          <Text>
                            {run.itemCountsByType
                              ? `Scenes ${run.itemCountsByType.scene || 0} • Quotes ${run.itemCountsByType.quote || 0} • Vibes ${run.itemCountsByType.vibe || 0}`
                              : 'No items recorded yet'}
                          </Text>
                          {run.overallTimePeriods?.length ? (
                            <Text type="secondary">
                              Time periods: {run.overallTimePeriods.join(', ')}
                            </Text>
                          ) : null}
                          {run.chapterGuids?.length ? (
                            <Text type="secondary">
                              Chapters: {run.chapterGuids.slice(0, 4).join(', ')}
                              {run.chapterGuids.length > 4 ? ` +${run.chapterGuids.length - 4} more` : ''}
                            </Text>
                          ) : null}
                          {run.error ? <Alert type="error" showIcon message={run.error} /> : null}
                        </Space>
                      </div>
                    ))}
                  </Space>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No extraction runs yet." />
                )}
              </Card>
            </Space>
          </Col>

          <Col xs={24} lg={14}>
            <Card
              size="small"
              title={
                <Space>
                  <EditOutlined />
                  <span>{editor?.id ? 'Edit Inspiration Item' : 'New Inspiration Item'}</span>
                </Space>
              }
              extra={
                <Space wrap>
                  {editor?.id ? (
                    <>
                      <Button
                        icon={<CheckOutlined />}
                        onClick={() => setEditor(prev => ({ ...(prev || createBlankItem()), status: 'approved' }))}
                      >
                        Approve
                      </Button>
                      <Button onClick={() => setEditor(prev => ({ ...(prev || createBlankItem()), status: 'draft' }))}>
                        Move to Draft
                      </Button>
                    </>
                  ) : null}
                  {editor?.id ? (
                    <Popconfirm
                      title="Delete inspiration item?"
                      description="This permanently removes the selected item."
                      onConfirm={() => void deleteItem(editor.id || '')}
                    >
                      <Button danger>Delete</Button>
                    </Popconfirm>
                  ) : null}
                  <Button type="primary" loading={saving} onClick={() => void saveItem()}>
                    Save Item
                  </Button>
                </Space>
              }
            >
              {editor ? (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={8}>
                      <Text strong>Type</Text>
                      <Select
                        value={editor.type || 'scene'}
                        onChange={value => updateEditor('type', value)}
                        style={{ width: '100%', marginTop: 6 }}
                        options={[
                          { value: 'scene', label: 'Scene' },
                          { value: 'quote', label: 'Quote' },
                          { value: 'vibe', label: 'Vibe' },
                        ]}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Status</Text>
                      <Select
                        value={editor.status || 'draft'}
                        onChange={value => updateEditor('status', value)}
                        style={{ width: '100%', marginTop: 6 }}
                        options={[
                          { value: 'draft', label: 'Draft' },
                          { value: 'approved', label: 'Approved' },
                          { value: 'skipped', label: 'Skipped' },
                        ]}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Spoiler Level</Text>
                      <Select
                        value={editor.spoilerLevel || 'medium'}
                        onChange={value => updateEditor('spoilerLevel', value)}
                        style={{ width: '100%', marginTop: 6 }}
                        options={[
                          { value: 'low', label: 'Low' },
                          { value: 'medium', label: 'Medium' },
                          { value: 'high', label: 'High' },
                        ]}
                      />
                    </Col>
                  </Row>

                  <div>
                    <Text strong>Title</Text>
                    <Input
                      value={editor.title || ''}
                      onChange={event => updateEditor('title', event.target.value)}
                      style={{ marginTop: 6 }}
                      placeholder="Short label for this inspiration item"
                    />
                  </div>

                  <div>
                    <Text strong>{editor.type === 'quote' ? 'Marketing Note' : 'Text'}</Text>
                    <TextArea
                      rows={editor.type === 'quote' ? 3 : 4}
                      value={editor.text || ''}
                      onChange={event => updateEditor('text', event.target.value)}
                      style={{ marginTop: 6 }}
                      placeholder={
                        editor.type === 'quote'
                          ? 'Why this quote is useful in marketing or how to use it'
                          : editor.type === 'scene'
                            ? 'Short scene summary'
                            : 'Compact vibe description'
                      }
                    />
                  </div>

                  {editor.type === 'quote' ? (
                    <div>
                      <Text strong>Exact Quote</Text>
                      <TextArea
                        rows={4}
                        value={editor.quoteText || ''}
                        onChange={event => updateEditor('quoteText', event.target.value)}
                        style={{ marginTop: 6 }}
                        placeholder="Exact source quote"
                      />
                    </div>
                  ) : null}

                  {editor.type === 'scene' ? (
                    <Card size="small" title="Scene Video Blueprint">
                      <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Row gutter={[12, 12]}>
                          <Col xs={24} md={12}>
                            <Text strong>Scene Mode</Text>
                            <Input
                              value={editor.sceneVideo?.sceneMode || ''}
                              onChange={event =>
                                updateEditor('sceneVideo', {
                                  ...(editor.sceneVideo || {}),
                                  sceneMode: event.target.value,
                                })
                              }
                              style={{ marginTop: 6 }}
                              placeholder="conversation, action, reveal, mood, montage..."
                            />
                          </Col>
                          <Col xs={24} md={12}>
                            <Text strong>Dialogue Overlay Lines</Text>
                            <Select
                              mode="tags"
                              value={editor.sceneVideo?.dialogueOverlayLines || []}
                              onChange={value =>
                                updateEditor('sceneVideo', {
                                  ...(editor.sceneVideo || {}),
                                  dialogueOverlayLines: value,
                                })
                              }
                              style={{ width: '100%', marginTop: 6 }}
                              placeholder="Short dialogue lines usable over slides"
                            />
                          </Col>
                        </Row>
                        <div>
                          <Text strong>10-12 Second Video Prompt</Text>
                          <TextArea
                            rows={4}
                            value={editor.sceneVideo?.prompt || ''}
                            onChange={event =>
                              updateEditor('sceneVideo', {
                                ...(editor.sceneVideo || {}),
                                prompt: event.target.value,
                              })
                            }
                            style={{ marginTop: 6 }}
                            placeholder="Write a 10-12 second video prompt with one or more beats or cuts."
                          />
                        </div>
                        <div>
                          <Text strong>Beat Outline</Text>
                          <Select
                            mode="tags"
                            value={editor.sceneVideo?.beats || []}
                            onChange={value =>
                              updateEditor('sceneVideo', {
                                ...(editor.sceneVideo || {}),
                                beats: value,
                              })
                            }
                            style={{ width: '100%', marginTop: 6 }}
                            placeholder="Add short beats/cuts for the clip"
                          />
                        </div>
                      </Space>
                    </Card>
                  ) : null}

                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}>
                      <Text strong>Characters</Text>
                      <Select
                        mode="tags"
                        value={editor.characterNames || []}
                        onChange={value => updateEditor('characterNames', value)}
                        style={{ width: '100%', marginTop: 6 }}
                        placeholder="Add characters"
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <Text strong>Locations</Text>
                      <Select
                        mode="tags"
                        value={editor.locationNames || []}
                        onChange={value => updateEditor('locationNames', value)}
                        style={{ width: '100%', marginTop: 6 }}
                        placeholder="Add locations"
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <Text strong>Time Periods</Text>
                      <Select
                        mode="tags"
                        value={editor.timePeriods || []}
                        onChange={value => updateEditor('timePeriods', value)}
                        style={{ width: '100%', marginTop: 6 }}
                        placeholder="Examples: Regency-era court, present day, childhood flashback"
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <Text strong>Tags</Text>
                      <Select
                        mode="tags"
                        value={editor.tags || []}
                        onChange={value => updateEditor('tags', value)}
                        style={{ width: '100%', marginTop: 6 }}
                        placeholder="Add tags"
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <Text strong>Vibe Tags</Text>
                      <Select
                        mode="tags"
                        value={editor.vibeTags || []}
                        onChange={value => updateEditor('vibeTags', value)}
                        style={{ width: '100%', marginTop: 6 }}
                        placeholder="Add vibe tags"
                      />
                    </Col>
                    <Col xs={24}>
                      <Text strong>Content Flags</Text>
                      <Select
                        mode="tags"
                        value={editor.contentFlags || []}
                        onChange={value => updateEditor('contentFlags', value)}
                        style={{ width: '100%', marginTop: 6 }}
                        placeholder="Examples: romance, violence, spicy, dark"
                      />
                    </Col>
                    <Col xs={24}>
                      <Text strong>Source Chapters</Text>
                      <Select
                        mode="multiple"
                        value={editor.chapterGuids || []}
                        onChange={value => updateEditor('chapterGuids', value)}
                        style={{ width: '100%', marginTop: 6 }}
                        options={chapters}
                        showSearch
                        optionFilterProp="label"
                        placeholder="Link this item to one or more chapters"
                      />
                    </Col>
                  </Row>

                  <div>
                    <Text strong>Source Excerpt</Text>
                    <TextArea
                      rows={3}
                      value={editor.sourceExcerpt || ''}
                      onChange={event => updateEditor('sourceExcerpt', event.target.value)}
                      style={{ marginTop: 6 }}
                      placeholder="Optional supporting excerpt from the source chapter"
                    />
                  </div>

                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={8}>
                      <Text strong>Character Studio Hint</Text>
                      <TextArea
                        rows={3}
                        value={editor.usageHints?.characterStudio || ''}
                        onChange={event =>
                          updateEditor('usageHints', {
                            ...(editor.usageHints || {}),
                            characterStudio: event.target.value,
                          })
                        }
                        style={{ marginTop: 6 }}
                        placeholder="How this should influence character art"
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Video Studio Hint</Text>
                      <TextArea
                        rows={3}
                        value={editor.usageHints?.videoStudio || ''}
                        onChange={event =>
                          updateEditor('usageHints', {
                            ...(editor.usageHints || {}),
                            videoStudio: event.target.value,
                          })
                        }
                        style={{ marginTop: 6 }}
                        placeholder="How this should influence video prompts"
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>Social Slides Hint</Text>
                      <TextArea
                        rows={3}
                        value={editor.usageHints?.socialSlides || ''}
                        onChange={event =>
                          updateEditor('usageHints', {
                            ...(editor.usageHints || {}),
                            socialSlides: event.target.value,
                          })
                        }
                        style={{ marginTop: 6 }}
                        placeholder="How this fits TikTok story slides or a carousel"
                      />
                    </Col>
                  </Row>

                  {editor.id ? (
                    <Alert
                      type="info"
                      showIcon
                      message={`Created ${formatTimestamp(editor.createdAt)}${editor.origin ? ` • ${editor.origin}` : ''}`}
                    />
                  ) : (
                    <Alert
                      type="info"
                      showIcon
                      message="New items are created as manual entries. Use Extract From Book for AI-generated drafts."
                    />
                  )}
                </Space>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select an item or create a new one." />
              )}
            </Card>
          </Col>
        </Row>
      </Space>

      <Modal
        title="Rerun Selected Chapters"
        open={rerunModalOpen}
        onCancel={() => setRerunModalOpen(false)}
        onOk={() => void queueExtraction(selectedRerunChapterGuids)}
        okText="Queue Rerun"
        okButtonProps={{ disabled: !selectedRerunChapterGuids.length, loading: saving }}
        destroyOnClose={false}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">
            Only AI-extracted items linked to these chapters will be replaced. Manual items and edited AI items stay untouched.
          </Text>
          <Select
            mode="multiple"
            value={selectedRerunChapterGuids}
            onChange={setSelectedRerunChapterGuids}
            style={{ width: '100%' }}
            options={chapters}
            showSearch
            optionFilterProp="label"
            placeholder="Select chapters to rerun"
          />
        </Space>
      </Modal>
    </Spin>
  );
};

export default MarketingInspirationModule;
